import type {
  ConfigurationOperation,
  LearningJourneyOperation,
} from "@repo/app-types/entities/configurationAgent";
import { isLearningJourneyOperation } from "@repo/app-types/helpers/configurationAgentParser";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { useCallback, useMemo, useState, type FC } from "react";
import { ConfigurationAssistantChat } from "../configurationAssistant/configurationAssistantChat.js";
import {
  ConfigurationAssistantContext,
  type ConfigurationAssistantContextValue,
} from "../configurationAssistant/configurationAssistantContext.js";

const DEFAULT_SUGGESTIONS = [
  "Create a new learning journey for this playbook.",
  "Review this learning journey for potential improvements.",
] as const;

export interface LearningJourneyConfigurationAssistantProps {
  onApplyOperation: (operation: LearningJourneyOperation) => Promise<void>;
  getEntityName: (id: string, type: "chapter" | "lesson") => string | undefined;
}

export const LearningJourneyConfigurationAssistant: FC<
  LearningJourneyConfigurationAssistantProps
> = ({ onApplyOperation: onApplyOperationProp, getEntityName }) => {
  const [appliedOperationIds, setAppliedOperationIds] = useState(
    () => new Set<string>(),
  );

  const onApplyOperation = useEffectEvent(
    async (
      operationId: string,
      operation: ConfigurationOperation,
    ): Promise<void> => {
      if (!isLearningJourneyOperation(operation)) {
        throw new Error("Operation type not supported by journey builder");
      }

      if (appliedOperationIds.has(operationId)) {
        return;
      }

      await onApplyOperationProp(operation);
      setAppliedOperationIds((prev) => new Set(prev).add(operationId));
    },
  );

  const isOperationApplied = useCallback(
    (operationId: string) => appliedOperationIds.has(operationId),
    [appliedOperationIds],
  );

  const contextValue: ConfigurationAssistantContextValue = useMemo(
    () => ({
      applyOperation: onApplyOperation,
      isOperationApplied,
      getEntityName: (id, type) => {
        if (type === "chapter" || type === "lesson") {
          return getEntityName(id, type);
        }

        return undefined;
      },
      getTestSpec: () => undefined,
      outcomeDefinition: undefined,
      getDocumentDisplayName: () => undefined,
    }),
    [onApplyOperation, isOperationApplied, getEntityName],
  );

  return (
    <ConfigurationAssistantContext.Provider value={contextValue}>
      <div className="flex min-h-0 flex-1 flex-col">
        <ConfigurationAssistantChat
          title="Edit configuration with AI"
          description="I can suggest chapter and lesson changes using the attached playbook and knowledge base."
          placeholder="Describe what you want to change..."
          suggestions={DEFAULT_SUGGESTIONS}
        />
      </div>
    </ConfigurationAssistantContext.Provider>
  );
};
